import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const MaintenanceContext = createContext();

export const useMaintenance = () => {
    const context = useContext(MaintenanceContext);
    if (!context) {
        throw new Error('useMaintenance must be used within a MaintenanceProvider');
    }
    return context;
};

export const MaintenanceProvider = ({ children }) => {
    const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
    const [loading, setLoading] = useState(true);

    const checkMaintenanceStatus = useCallback(async () => {
        try {
            // Cache-busting: appending a unique timestamp to the request URL
            const response = await fetch(`/app-config.json?t=${new Date().getTime()}`, {
                method: 'GET',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch config');
            }

            const config = await response.json();
            const maintenanceEnabled = config?.maintenance?.enabled ?? false;
            const refreshIntervalMs = config?.maintenance?.refreshIntervalMs ?? 30000;

            if (maintenanceEnabled !== isMaintenanceMode) {
                setIsMaintenanceMode(maintenanceEnabled);
                // Optional: If maintenance just ended, you might want to refresh the page to clear stale states
                // if (isMaintenanceMode && !maintenanceEnabled) {
                //     window.location.reload();
                // }
            }
            
            return refreshIntervalMs;
        } catch (error) {
            console.error('Error fetching maintenance config:', error);
            return 30000; // Fallback to 30s polling on error
        } finally {
            setLoading(false);
        }
    }, [isMaintenanceMode]);

    useEffect(() => {
        let timeoutId;

        const poll = async () => {
            const interval = await checkMaintenanceStatus();
            timeoutId = setTimeout(poll, interval);
        };

        poll();

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [checkMaintenanceStatus]);

    return (
        <MaintenanceContext.Provider value={{ isMaintenanceMode, loading, checkMaintenanceStatus }}>
            {children}
        </MaintenanceContext.Provider>
    );
};
